<template>
  <Dialog
    v-model:visible="isVisible"
    :modal="true"
    :style="{ width: '460px' }"
    :header="dialogHeader"
    class="billable-action-dialog"
  >
    <div class="billable-action">
      <div
        class="billable-action__icon"
        :class="`billable-action__icon--${mode}`"
      >
        <i :class="mode === 'billable' ? 'fa-solid fa-circle-check' : 'fa-solid fa-ban'" />
      </div>
      <p class="billable-action__description">
        {{ mode === 'billable'
          ? t('myLists.actions.setBillable.description')
          : t('myLists.actions.setNonBillable.description') }}
      </p>
    </div>

    <template #footer>
      <SpmButton
        :label="t('common.cancel')"
        icon="pi pi-times"
        class-style="p-button p-button-secondary"
        @click="close"
      />
      <SpmButton
        :label="t('validate')"
        icon="pi pi-check"
        :class-style="mode === 'billable' ? 'p-button p-button-success' : 'p-button p-button-warning'"
        @click="confirm"
      />
    </template>
  </Dialog>
</template>

<script lang="ts">
import {
  defineComponent, ref, computed, PropType,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'primevue/dialog';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import type { ActionPayload, BillableMode, SetBillableResult } from './types';

export default defineComponent({
  name: 'SetBillableAction',

  components: {
    Dialog,
    SpmButton,
  },

  props: {
    /** 'billable' => rend facturable (do_not_charge=0) ; 'non_billable' => exclut (do_not_charge=1). */
    mode: {
      type: String as PropType<BillableMode>,
      required: true,
    },
  },

  emits: ['confirm'],

  setup(props, { emit, expose }) {
    const { t } = useI18n();

    const isVisible = ref(false);
    const currentPayload = ref<ActionPayload | null>(null);

    // Header dépend du sens (billable / non-billable) ET du contexte (lot / ligne).
    const dialogHeader = computed(() => {
      const base = props.mode === 'billable'
        ? 'myLists.actions.setBillable'
        : 'myLists.actions.setNonBillable';
      return currentPayload.value?.context === 'grouped'
        ? t(`${base}.headerGrouped`)
        : t(`${base}.headerRow`);
    });

    const open = (payload: ActionPayload) => {
      currentPayload.value = payload;
      isVisible.value = true;
    };

    const close = () => {
      isVisible.value = false;
      currentPayload.value = null;
    };

    const confirm = () => {
      if (!currentPayload.value) return;

      const result: SetBillableResult = {
        context: currentPayload.value.context,
        mode: props.mode,
        selectionInfo: currentPayload.value.selectionInfo,
        rowId: currentPayload.value.rowId,
      };

      emit('confirm', result);
      close();
    };

    expose({ open });

    return {
      t,
      isVisible,
      dialogHeader,
      close,
      confirm,
    };
  },
});
</script>

<style scoped lang="scss">
.billable-action {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0.5rem 0 0.25rem;

  &__icon {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    margin-bottom: 1rem;

    &--billable {
      background: #ecfdf5;
      color: #047857;
    }

    &--non_billable {
      background: #fffbeb;
      color: #b45309;
    }
  }

  &__description {
    margin: 0;
    color: #475569;
    font-size: 0.95rem;
    line-height: 1.5;
    text-align: center;
  }
}
</style>
